import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../../utils/googleMaps';
import { MapPin, Clock, Truck, Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function FloristsMarrickville() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        
        if (mapRef.current && window.google) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: -33.9113, lng: 151.1553 }, // Marrickville coordinates
            zoom: 14,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });

          // Add marker for Marrickville
          new google.maps.Marker({
            position: { lat: -33.9113, lng: 151.1553 },
            map: map,
            title: "Marrickville",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "brand-primary",
              fillOpacity: 0.8,
              strokeColor: "#fff",
              strokeWeight: 2
            }
          });
        }
      } catch (error) {
        console.error('Error loading map:', error);
        setMapError(true);
      }
    };

    initMap();
  }, []);

  const marrickvilleSearchUrl = "/search?location=Marrickville+NSW+2204%2C+Australia&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.9113&lng=151.1553";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do Marrickville florists offer same-day delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, many Marrickville-based florists offer same-day delivery when orders are placed before 2pm on weekdays."
        }
      },
      {
        "@type": "Question",
        "name": "How much does flower delivery in Marrickville cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Delivery costs typically range from $6 to $14, depending on distance, florist, and delivery time."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do Marrickville florists deliver to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most florists in Marrickville deliver to surrounding Inner West suburbs including Newtown, Dulwich Hill, Petersham, and Stanmore."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Florists Marrickville | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Browse the top florists in Marrickville NSW offering same-day delivery. From multicultural celebrations to contemporary designs, MarketBloom connects you with the Inner West's finest florists." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Local Guides</span>
              <span>•</span>
              <span>15 January 2025</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Florists in Marrickville for Same-Day Delivery 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/best-florists-marrickville-same-day-hero.jpg" 
              alt="Multicultural floral display at a diverse Marrickville florist on Victoria Road"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding authentic local florists in Marrickville NSW who deliver same-day shouldn't mean settling for overpriced corporate chains or generic supermarket bouquets.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with independent Marrickville flower shops - the multicultural artisans scattered between Victoria Road's diverse eateries and Addison Road Community Centre who craft arrangements that celebrate our suburb's rich cultural tapestry.
            </p>
            <p className="leading-7 text-black">
              Whether you need urgent flower delivery to Marrickville Metro, cultural celebration flowers for a Greek Orthodox ceremony, or contemporary arrangements delivered to a Dulwich Hill home, our network of local florists ensures fresh, beautiful blooms arrive on time - often within hours of ordering.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Marrickville's finest florists offering same-day delivery options</p>
            <Link 
              to={`${marrickvilleSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=marrickville-florists`}
              className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Marrickville Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Marrickville Florists Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">What Makes Marrickville Florists Special</h2>
            
            <p className="leading-7 text-black mb-6">
              Marrickville's independent florists are as diverse as the suburb itself. From the established flower shops near Marrickville Station to the boutique studios scattered along Victoria Road, these local artisans understand what Inner West residents love - multicultural celebration flowers, sustainable practices, and designs that honor both traditional heritage and contemporary Australian style.
            </p>
            <p className="leading-7 text-black mb-6">
              Unlike generic flower delivery services that ship from distant warehouses, Marrickville florists source from Sydney Flower Market daily, ensuring peak freshness. They know that a bouquet for a Vietnamese festival needs specific cultural elements, and arrangements for Addison Road Community Centre events should reflect the area's creative, inclusive spirit.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for multicultural expertise" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Multicultural Expertise</h3>
                <p className="text-black">From Greek to Vietnamese traditions</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for Inner West knowledge" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Inner West Knowledge</h3>
                <p className="text-black">Know every street and community</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for same-day service" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Same-Day Service</h3>
                <p className="text-black">Available from multiple local florists</p>
              </div>
            </div>
          </div>

          {/* Same-Day Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Delivery Coverage</h2>
            
            <p className="leading-7 text-black mb-6">
              Many of Marrickville's best florists offer same-day delivery with varying cutoff times throughout the day. This service covers not just Marrickville (2204) but often extends to neighboring Inner West suburbs.
            </p>

            <div className="mb-6">
              <div 
                ref={mapRef} 
                className="w-full h-80 rounded-lg shadow-md bg-gray-200"
              >
                {mapError && (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <MapPin className="w-8 h-8 mr-2" />
                    <span>Map unavailable - Marrickville NSW 2204</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Marrickville</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Newtown</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Dulwich Hill</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Truck className="w-5 h-5 text-brand-primary" aria-label="Delivery truck icon" />
                <span className="font-medium text-black">Petersham</span>
              </div>
            </div>
          </div>

          {/* Popular Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Popular Flower Styles in Marrickville</h2>
            
            <p className="leading-7 text-black mb-6">
              The best Marrickville florists understand their diverse clientele perfectly. Greek families love traditional arrangements with white roses and baby's breath for Orthodox celebrations. Vietnamese communities appreciate vibrant chrysanthemums and lotus-inspired designs. Italian families prefer classic European styles with rich colors. And the growing young professional community seeks contemporary arrangements that reflect modern Australian aesthetics.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-4">Most Requested Styles:</h3>
              <ul className="my-6 ml-6 list-disc [&>li]:mt-2">
                <li className="text-black">Traditional European arrangements for cultural celebrations</li>
                <li className="text-black">Asian-inspired designs with chrysanthemums and orchids</li>
                <li className="text-black">Contemporary Australian arrangements with native flowers</li>
                <li className="text-black">Vibrant multicultural celebration bouquets</li>
              </ul>
            </div>
          </div>

          {/* Why Choose MarketBloom for Marrickville Flower Delivery */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Local Marrickville Florists</h2>
            
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Supporting Inner West Multicultural Businesses</h3>
                  <p className="leading-7 text-black">When you order through MarketBloom, 100% of your flower purchase goes directly to independent Marrickville florists. Unlike corporate chains that take hefty commissions, we believe in empowering local artisans who know every corner from Victoria Road to Addison Road Community Centre.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Real-Time Availability from Multiple Florists</h3>
                  <p className="leading-7 text-black">Our platform connects you with several Marrickville flower shops simultaneously, showing live inventory and same-day delivery slots. This means better selection for last-minute orders and competitive pricing from florists competing for your business.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Transparent Reviews from Real Marrickville Customers</h3>
                  <p className="leading-7 text-black">Every florist review on MarketBloom comes from verified local deliveries. Read honest feedback from your Marrickville neighbours about flower quality, delivery timing, and customer service before choosing your florist.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Efficient Inner West Delivery Network</h3>
                  <p className="leading-7 text-black">Shorter delivery distances mean fresher flowers and faster service. Our Marrickville florists understand the unique logistics of the Inner West, from busy Victoria Road to quiet residential streets, ensuring your bouquet arrives in perfect condition.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-[#967F81] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Celebrating Marrickville's Cultural Diversity</h3>
                  <p className="leading-7 text-black">By choosing local florists over mass-market alternatives, you're supporting businesses that understand and celebrate cultural traditions from around the world. These artisan florists create arrangements that honor heritage while embracing contemporary Australian life.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Linking Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Explore Florists in Other Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Looking for flowers elsewhere in Sydney? MarketBloom connects you with the best local florists across the city:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/search?location=Newtown+NSW+2042&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8978149&lng=151.1785003&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Newtown florists - bohemian blooms and creative arrangements
              </Link>
              <Link to="/search?location=Bondi+NSW+2026&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8915&lng=151.2767&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Bondi florists - beachside blooms and coastal arrangements
              </Link>
              <Link to="/search?location=Parramatta+NSW+2150&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8175438&lng=151.0000257&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Parramatta flower delivery - Western Sydney's finest
              </Link>
              <Link to="/search?location=Surry+Hills+NSW+2010&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8886109&lng=151.2094393&occasions=Birthday&occasions=Anniversary&occasions=Wedding&occasions=Sympathy&occasions=Get+Well&occasions=Congratulations&occasions=Thank+You&occasions=New+Baby&occasions=Valentine%27s+Day&occasions=Mother%27s+Day&occasions=Christmas&occasions=Graduation&occasions=Housewarming&occasions=Corporate&categories=Bouquet&categories=Floral+Arrangement&categories=Potted+Plant&categories=Single+Flower&categories=Other" className="text-brand-primary hover:underline">
                • Surry Hills florist listings - trendy inner-city options
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg px-6 py-5 mb-8 shadow-sm">
            <blockquote className="italic text-black">
              <p className="leading-7 mb-2">
                "MarketBloom helped me send traditional Greek flowers for my grandmother's name day celebration within 3 hours — the florist understood exactly what we needed!"
              </p>
              <footer className="text-sm font-medium not-italic">
                – Maria P., Marrickville
              </footer>
            </blockquote>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 0}
                  aria-controls="faq-answer-0"
                >
                  <h3 className="text-xl font-semibold text-black">Do Marrickville florists offer same-day delivery?</h3>
                  {openFaq === 0 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-0" className={openFaq === 0 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Yes, many Marrickville-based florists offer same-day delivery when orders are placed before 2pm on weekdays.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <h3 className="text-xl font-semibold text-black">How much does flower delivery in Marrickville cost?</h3>
                  {openFaq === 1 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-1" className={openFaq === 1 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Delivery costs typically range from $6 to $14, depending on distance, florist, and delivery time.
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="flex justify-between items-center w-full text-left p-2 hover:bg-gray-50 rounded transition-colors min-h-[48px]"
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <h3 className="text-xl font-semibold text-black">What areas do Marrickville florists deliver to?</h3>
                  {openFaq === 2 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                <div id="faq-answer-2" className={openFaq === 2 ? 'block' : 'hidden'}>
                  <p className="mt-3 leading-7 text-black px-2">
                    Most florists in Marrickville deliver to surrounding Inner West suburbs including Newtown, Dulwich Hill, Petersham, and Stanmore.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-[brand-primary] to-[#E63E1C] text-white rounded-lg p-8 text-center md:relative sticky bottom-0 z-50">
            <h2 className="scroll-m-20 text-3xl font-semibold tracking-tight text-white mb-4">Ready to Order Beautiful Flowers?</h2>
            <p className="text-xl leading-7 text-white mb-6 opacity-90">
              Browse Marrickville's premier florists offering same-day delivery on multicultural arrangements
            </p>
            <Link 
              to={`${marrickvilleSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=marrickville-florists`}
              className="bg-white text-brand-primary px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-3 text-lg min-h-[48px]"
            >
              Shop Marrickville Florists Now
              <ArrowRight className="w-6 h-6" />
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight text-black mb-6">Related Articles</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/blog/birthday-flowers-sydney" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Birthday Flowers Sydney: Best Options for Every Age
                  </h4>
                  <p className="leading-7 text-black">Find the perfect birthday flowers for any celebration</p>
                </div>
              </Link>
              <Link to="/blog/local-vs-corporate-florists" className="group">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2 group-hover:text-brand-primary transition-colors">
                    Local vs Corporate Florists: Why Local Wins
                  </h4>
                  <p className="leading-7 text-black">Discover the benefits of choosing local Sydney florists</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 